import { useGLTF, useTexture, Text } from "@react-three/drei";
import { useEffect, useState, useMemo } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import tvVertexShader from "./shaders/tv/vertex.glsl";
import tvFragmentShader from "./shaders/tv/fragment.glsl";
import { shaderMaterial } from "@react-three/drei";
import { extend } from "@react-three/fiber";
import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useAudioStore } from "./stores/useAudioStore";
import { Float } from "@react-three/drei";
const TvMaterial = shaderMaterial(
  {
    uTime: { value: 0 },
    uBackgroundColor: { value: new THREE.Color("#ff0000") },
  },
  tvVertexShader,
  tvFragmentShader
);

extend({ TvMaterial });

// Preload assets
useGLTF.preload("./assets/CozyBarTer.glb");
useTexture.preload("./assets/bake_bigA_4096.jpg");
useTexture.preload("./assets/bake_medA_4096.jpg");
useTexture.preload("./assets/bake_smaA_2048.jpg");

export default function BarScene() {
  const { gl } = useThree();
  const { nodes } = useGLTF("./assets/CozyBarTer.glb");

  // Compute TV screen local center and size (for precise text placement)
  const { tvCenter, tvSize } = useMemo(() => {
    const geo = nodes.TVScreen001?.geometry;
    if (!geo)
      return {
        tvCenter: new THREE.Vector3(),
        tvSize: new THREE.Vector3(1, 0.5, 0.01),
      };
    geo.computeBoundingBox();
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    geo.boundingBox.getCenter(center);
    geo.boundingBox.getSize(size);
    return { tvCenter: center, tvSize: size };
  }, [nodes]);

  const { currentMood, nextMood, isTransitioning } = useAudioStore();

  // Charger les textures du mood actuel
  const MergedBigTexture = useTexture(currentMood.textures.big);
  const MergedMedTexture = useTexture(currentMood.textures.med);
  const MergedSmaTexture = useTexture(currentMood.textures.sma);

  const tvMaterial = useRef();
  const [tvColor, setTvColor] = useState(new THREE.Color(currentMood.tvColor));
  const lastClickTime = useRef(0);

  // Mettre à jour la couleur de la TV quand le mood change
  useEffect(() => {
    setTvColor(new THREE.Color(currentMood.tvColor));
  }, [currentMood]);

  useFrame((state, delta) => {
    if (tvMaterial.current) {
      tvMaterial.current.uTime += delta;
      tvMaterial.current.uBackgroundColor = tvColor;
    }
  });

  for (const tex of [MergedBigTexture, MergedMedTexture, MergedSmaTexture]) {
    tex.flipY = false;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = gl.capabilities.getMaxAnisotropy();
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.needsUpdate = true;
  }

  const handleTVClick = (event) => {
    event.stopPropagation();

    // Ignorer les clics pendant la transition
    if (isTransitioning) {
      return;
    }

    const now = Date.now();
    const timeSinceLastClick = now - lastClickTime.current;

    // Cooldown de 500ms pour éviter les double-clics
    if (timeSinceLastClick < 500) {
      return;
    }

    lastClickTime.current = now;
    nextMood();
  };

  const handleTVPointerEnter = () => {
    if (!isTransitioning) {
      document.body.style.cursor = "pointer";
    }
  };

  const handleTVPointerLeave = () => {
    document.body.style.cursor = "auto";
  };

  return (
    <>
      <group rotation={[0, -Math.PI * 1.2, 0]} position={[-0.1, -0.8, -1.8]}>
        <mesh
          geometry={nodes.BakeBig2.geometry}
          position={nodes.BakeBig2.position}
        >
          <meshStandardMaterial
            map={MergedBigTexture}
            roughness={0.9}
            metalness={0}
          />
        </mesh>
        <mesh
          geometry={nodes.BakeMed2.geometry}
          position={nodes.BakeMed2.position}
        >
          <meshStandardMaterial
            map={MergedMedTexture}
            roughness={0.9}
            metalness={0}
          />
        </mesh>
        <mesh
          geometry={nodes.BakeSmall2.geometry}
          position={nodes.BakeSmall2.position}
        >
          <meshStandardMaterial
            map={MergedSmaTexture}
            roughness={0.9}
            metalness={0}
          />
        </mesh>
        {/* TV screen + overlayed Text anchored to its local center */}
        <mesh
          geometry={nodes.TVScreen001.geometry}
          position={[
            nodes.TVScreen001.position.x - 0.001,
            nodes.TVScreen001.position.y,
            nodes.TVScreen001.position.z,
          ]}
          rotation={nodes.TVScreen001.rotation}
          scale={nodes.TVScreen001.scale}
          onClick={handleTVClick}
          onPointerEnter={handleTVPointerEnter}
          onPointerLeave={handleTVPointerLeave}
        >
          <tvMaterial ref={tvMaterial} uTime={0} uBackgroundColor={tvColor} />
          <Text
            position={[tvCenter.x - 0.01, tvCenter.y, tvCenter.z]}
            anchorX="center"
            anchorY="middle"
            rotation={[0, -Math.PI / 2, 0]}
            fontSize={tvSize.y * 0.2}
            maxWidth={tvSize.x * 0.9}
            color="#444444"
            fontWeight="bold"
            pointerEvents="none"
          >
            {currentMood.tvText}
          </Text>
        </mesh>
      </group>
    </>
  );
}
